import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  Gauge, ChartLineUp, Cpu, Lightning, ShieldCheck, SignOut, House, Plugs,
  Crown, Sparkle, ShoppingCart, ChatCircle, Lock
} from "@phosphor-icons/react";

const SECTIONS = [
  { to: "/admin", end: true, label: "OVERVIEW", Icon: Gauge },
  { to: "/admin/subscriptions", label: "SUBSCRIPTIONS", Icon: Crown },
  { to: "/admin/analytics", label: "USER ANALYTICS", Icon: ChartLineUp },
  { to: "/admin/plugin", label: "PLUGIN CONTROL", Icon: Cpu },
  { to: "/admin/ai-engine", label: "AI ENGINE", Icon: Sparkle },
  { to: "/admin/marketplace", label: "MARKETPLACE", Icon: ShoppingCart },
  { to: "/admin/community", label: "COMMUNITY", Icon: ChatCircle },
  { to: "/admin/security", label: "SECURITY", Icon: Lock },
  { to: "/admin/webhooks", label: "WEBHOOKS", Icon: Plugs },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading) {
      if (!user) navigate("/auth?mode=login&redirect=/admin");
      else if (!user.is_admin) navigate("/dashboard");
    }
  }, [loading, user, navigate]);

  if (loading || !user || !user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center text-ink-dim">
        VERIFYING ADMIN CLEARANCE…
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-bg text-white" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-60 border-r border-zinc-900 bg-black flex flex-col sticky top-0 h-screen">
        <div className="p-5 border-b border-zinc-900">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={18} weight="fill" className="text-brand-red" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-brand-red">ADMIN OS</span>
          </div>
          <div className="font-display font-black text-xl tracking-tighter">
            BEAT-LAB <span className="text-brand-red">/</span>
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim mt-1">CONTROL ROOM</div>
        </div>

        <nav className="p-3 flex-1 space-y-1">
          {SECTIONS.map(({ to, end, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-[11px] tracking-[0.25em] uppercase border-l-2 transition-all ${
                  isActive
                    ? "border-brand-red text-white bg-brand-red/10"
                    : "border-transparent text-ink-mute hover:text-white hover:bg-zinc-900"
                }`
              }
            >
              <Icon size={16} weight="bold" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-zinc-900 space-y-2">
          <div className="px-3 py-2 border border-zinc-900">
            <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim">SIGNED IN AS</div>
            <div className="font-display text-sm font-bold truncate">{user.producer_name}</div>
            <div className="text-[10px] text-ink-dim truncate mt-0.5">{user.email}</div>
          </div>
          <NavLink to="/" className="flex items-center gap-2 px-3 py-2 text-[10px] tracking-[0.3em] uppercase text-ink-mute hover:text-white">
            <House size={12} weight="bold" /> EXIT TO SITE
          </NavLink>
          <button
            onClick={() => { logout(); navigate("/"); }}
            data-testid="admin-logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-[10px] tracking-[0.3em] uppercase text-ink-mute hover:text-brand-red"
          >
            <SignOut size={12} weight="bold" /> SIGN OUT
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Top bar */}
        <div className="h-12 border-b border-zinc-900 bg-bg-surface flex items-center justify-between px-6 sticky top-0 z-30 backdrop-blur bg-black/80">
          <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase">
            <span className="w-1.5 h-1.5 bg-brand-green rounded-full animate-pulse" />
            <span className="text-brand-green">SYSTEMS NOMINAL</span>
            <span className="text-ink-dim hidden sm:inline">· INGRESS OK · DB OK · SQUARE OK · LATENCY 0.04s</span>
          </div>
          <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-ink-dim">
            <Plugs size={12} weight="bold" /> BUILD 1.0.4 STABLE
            <Lightning size={12} weight="fill" className="text-brand-red ml-2" /> LIVE
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
